import type { Control, FieldValues } from "react-hook-form";
import type { Path } from "react-hook-form";
import type { UseFormWatch } from "react-hook-form";

import CurrencyInput from "~/components/formElements/CurrencyInput";
import FormSection from "~/components/formElements/FormSection";
import Input from "~/components/formElements/Input";
import Hint from "~/components/Hint";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";

type OrderBudgetFormSectionProps<T extends FieldValues> = {
	// The top-up slices of the order's quotas. They add to the entered budget.
	budgetCentsFromQuotas?: number;
	control: Control<T>;
	placeholder?: string;
	showQuotaHint?: boolean;
	watch: UseFormWatch<T>
};

type OrderBudgetFormSectionType = {
	manDays: number;
	budgetCents: number;
};

const OrderBudgetFormSection = <T extends OrderBudgetFormSectionType>({
	budgetCentsFromQuotas = 0,
	control,
	showQuotaHint = false,
	watch,
}: OrderBudgetFormSectionProps<T>) => {
	const manDaysPlanned = watch("manDays" as Path<T>);
	const budgetCentsPlanned = watch("budgetCents" as Path<T>);
	const averageDailyRateCents = manDaysPlanned && budgetCentsPlanned ? budgetCentsPlanned / manDaysPlanned : undefined;

	return (
		<FormSection title="Budget">
			<div className="grid grid-cols-6 gap-x-6 gap-y-2">
				<div className="col-span-3">
					<Input
						name="manDays"
						control={control}
						label="Personentage"
						type={"number"}
					/>
				</div>
				<div className="col-span-3">
					<CurrencyInput
						name={"budgetCents"}
						control={control}
						label="Budget (EUR)"
					/>
				</div>
				<div className="col-span-6 text-sm text-gray-500">
					Ø Tagessatz: {averageDailyRateCents ? formatCentsToCurrency(averageDailyRateCents) : "--"}
				</div>
				{budgetCentsFromQuotas > 0 && <dl className="col-span-6 mt-2 grid w-full grid-cols-[auto_auto] gap-x-8 text-sm text-gray-500">
					<dt>Budget der Bestellung</dt>
					<dd className="text-right tabular-nums">{formatCentsToCurrency(budgetCentsPlanned || 0)}</dd>
					<dt>Kontingente</dt>
					<dd className="text-right tabular-nums">{formatCentsToCurrency(budgetCentsFromQuotas)}</dd>
					<dt className="mt-1 border-t border-gray-200 pt-1 font-medium text-gray-700">Gesamtbudget</dt>
					<dd className="mt-1 border-t border-gray-200 pt-1 text-right font-medium tabular-nums text-gray-700">
						{formatCentsToCurrency((budgetCentsPlanned || 0) + budgetCentsFromQuotas)}
					</dd>
				</dl>}
				{showQuotaHint && <div className="col-span-6 text-sm text-gray-500">
					<Hint size="sm">
						<p>Da diese Phase einem Abrechnungskontingent zugewiesen ist, dienen die Budgetwerte
							lediglich zu Planungszwecken.</p>
					</Hint>
				</div>}
			</div>
		</FormSection>
	);
};

export default OrderBudgetFormSection;
